import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Paper,
  Chip,
  IconButton,
  Tooltip
} from '@mui/material';
import { Sync as SyncIcon, Undo as UndoIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { formatCurrencyPickerLabel } from '../utils/iso4217';

type RateRow = {
  quoteCurrency: string;
  syncedRateVsEur: string | null;
  rateVsEur: string;
  manualOverride: boolean;
};

const AdminReferenceFx: React.FC = () => {
  const navigate = useNavigate();
  const [checkingAccess, setCheckingAccess] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [rates, setRates] = useState<RateRow[]>([]);
  const [metadata, setMetadata] = useState<{
    lastSyncAt: string | null;
    lastSyncStatus: string | null;
    lastSyncError: string | null;
  } | null>(null);
  const [schedule, setSchedule] = useState<{
    nextAutoSyncAt?: string;
    description?: string;
    autoSyncHourUtc?: number;
    timezone?: string;
  } | null>(null);
  const ratesDirtyRef = useRef(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.getPlatformFx();
      const data = res?.data ?? res;
      const raw = (data?.rates as Record<string, unknown>[]) || [];
      ratesDirtyRef.current = false;
      setRates(
        raw.map((r) => ({
          quoteCurrency: String(r.quoteCurrency || ''),
          syncedRateVsEur: r.syncedRateVsEur != null ? String(r.syncedRateVsEur) : null,
          rateVsEur: String(r.rateVsEur ?? ''),
          manualOverride: !!r.manualOverride
        }))
      );
      setMetadata(data?.metadata ?? null);
      setSchedule(
        (data?.schedule as {
          nextAutoSyncAt?: string;
          description?: string;
          autoSyncHourUtc?: number;
          timezone?: string;
        }) ?? null
      );
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load reference FX');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const run = async () => {
      try {
        setCheckingAccess(true);
        const profile = await api.getProfile();
        if (profile.data?.user?.systemRole !== 'PLATFORM_ADMIN') {
          navigate('/dashboard');
          return;
        }
        await load();
      } catch {
        navigate('/dashboard');
      } finally {
        setCheckingAccess(false);
      }
    };
    run();
  }, [navigate, load]);

  useEffect(() => {
    if (!ratesDirtyRef.current || rates.length === 0) return undefined;
    const timer = window.setTimeout(async () => {
      try {
        await api.putPlatformFxRates(
          rates.map((r) => ({
            quoteCurrency: r.quoteCurrency,
            rateVsEur: r.rateVsEur,
            manualOverride: r.manualOverride
          }))
        );
        setSuccess('Published rates saved.');
        ratesDirtyRef.current = false;
      } catch (err: any) {
        setError(err.response?.data?.message || 'Failed to save rates');
        ratesDirtyRef.current = false;
        await load();
      }
    }, 900);
    return () => window.clearTimeout(timer);
  }, [rates, load]);

  const handleSync = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      await api.syncPlatformFx();
      setSuccess('Sync completed. Manual published rates were preserved where marked.');
      await load();
    } catch (err: any) {
      setError(err.response?.data?.message || err.response?.data?.data?.error || 'Sync failed');
      await load();
    } finally {
      setLoading(false);
    }
  };

  const updateEffective = (index: number, rateVsEur: string) => {
    ratesDirtyRef.current = true;
    setRates((prev) =>
      prev.map((r, i) => (i === index ? { ...r, rateVsEur, manualOverride: true } : r))
    );
  };

  const onEffectiveBlur = (index: number) => {
    setRates((prev) => {
      const r = prev[index];
      if (!r) return prev;
      const s = (r.syncedRateVsEur || '').trim();
      const e = r.rateVsEur.trim();
      if (s && e === s) {
        ratesDirtyRef.current = true;
        return prev.map((row, i) => (i === index ? { ...row, manualOverride: false } : row));
      }
      return prev;
    });
  };

  const revertToSync = (index: number) => {
    ratesDirtyRef.current = true;
    setRates((prev) => {
      const r = prev[index];
      if (!r || r.syncedRateVsEur == null) return prev;
      return prev.map((row, i) =>
        i === index
          ? { ...row, rateVsEur: r.syncedRateVsEur as string, manualOverride: false }
          : row
      );
    });
  };

  if (checkingAccess) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  const stale =
    metadata?.lastSyncStatus === 'error' ||
    !metadata?.lastSyncAt ||
    (metadata?.lastSyncError && metadata.lastSyncError.length > 0);

  const lastSyncLabel = metadata?.lastSyncAt
    ? new Date(metadata.lastSyncAt).toLocaleString(undefined, {
        dateStyle: 'medium',
        timeStyle: 'short'
      })
    : null;

  return (
    <Box p={3}>
      <Typography variant="h4" component="h1" gutterBottom>
        Currency
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1, maxWidth: 900 }}>
        Platform-wide reference exchange rates (EUR-based: units of quote currency per 1 EUR). The{' '}
        <strong>last provider sync</strong> column updates when you sync or when the daily job runs. The{' '}
        <strong>published</strong> column is what organizations use; if you change it, daily sync updates the sync
        column only and does not overwrite your published value.
      </Typography>

      <Box display="flex" flexWrap="wrap" alignItems="center" gap={1} sx={{ mb: 2 }}>
        {lastSyncLabel ? (
          <Chip
            size="small"
            color={metadata?.lastSyncStatus === 'success' ? 'success' : 'default'}
            label={`Last provider sync: ${lastSyncLabel}`}
            variant="outlined"
          />
        ) : (
          <Chip size="small" label="No provider sync recorded yet" variant="outlined" />
        )}
        {metadata?.lastSyncStatus && (
          <Chip size="small" label={`Status: ${metadata.lastSyncStatus}`} variant="outlined" />
        )}
        {schedule?.nextAutoSyncAt && (
          <Chip
            size="small"
            color="info"
            variant="outlined"
            label={`Next auto sync (UTC): ${new Date(schedule.nextAutoSyncAt).toLocaleString(undefined, {
              dateStyle: 'medium',
              timeStyle: 'short',
              timeZone: 'UTC'
            })}`}
          />
        )}
      </Box>

      {stale && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Last sync may be stale or failed.
          {metadata?.lastSyncError && ` ${metadata.lastSyncError}`}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Box display="flex" gap={2} mb={2} alignItems="center" flexWrap="wrap">
        <Button variant="contained" startIcon={<SyncIcon />} onClick={handleSync} disabled={loading}>
          Sync from provider
        </Button>
        <Typography variant="caption" color="text.secondary">
          Published values save automatically shortly after you edit them.
        </Typography>
      </Box>

      <Card>
        <CardContent>
          {loading && rates.length === 0 ? (
            <CircularProgress size={32} />
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ minWidth: 220 }}>Currency</TableCell>
                    <TableCell sx={{ minWidth: 160 }}>Last sync (read-only)</TableCell>
                    <TableCell sx={{ minWidth: 200 }}>Published (used by orgs)</TableCell>
                    <TableCell align="right" width={100}>
                      Override
                    </TableCell>
                    <TableCell align="center" width={72}>
                      Reset
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rates.map((row, index) => (
                    <TableRow key={row.quoteCurrency}>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {formatCurrencyPickerLabel(row.quoteCurrency)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary" fontFamily="monospace">
                          {row.syncedRateVsEur ?? '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          fullWidth
                          value={row.rateVsEur}
                          onChange={(e) => updateEffective(index, e.target.value)}
                          onBlur={() => onEffectiveBlur(index)}
                          disabled={loading}
                          inputProps={{ 'aria-label': `Published rate ${row.quoteCurrency}` }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        {row.manualOverride ? (
                          <Chip size="small" color="primary" label="Manual" variant="outlined" />
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            Auto
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Copy last sync value into published and clear manual override">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => revertToSync(index)}
                              disabled={loading || row.syncedRateVsEur == null}
                              aria-label={`Reset ${row.quoteCurrency} to synced value`}
                            >
                              <UndoIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default AdminReferenceFx;
